import { useEffect, useState } from 'react';
import { useKioskAutoReload } from './hooks/useKioskAutoReload';
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { fetchAuthMe } from './api/client';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CalendarPage } from './pages/Calendar';
import { ChoresPage } from './pages/Chores';
import { ListsPage } from './pages/Lists';
import { SettingsPage } from './pages/Settings';
import { AdminPage } from './pages/Admin';
import { LoginPage } from './pages/Login';
import { OnboardingPage } from './pages/Onboarding';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { InviteExpiredPage } from './pages/InviteExpired';
import { InviteConflictPage } from './pages/InviteConflict';
import { PrivacyPage } from './pages/Privacy';
import { TermsPage } from './pages/Terms';
import { LandingPage } from './pages/Landing';
import { TestAddressAutocompletePage } from './pages/TestAddressAutocomplete';
import { PairPage } from './pages/Pair';
import { KioskReconnectOverlay } from './components/KioskReconnectOverlay';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Component to handle OAuth redirect with isNewUser query param
function AuthRedirectHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNewUser = searchParams.get('isNewUser') === 'true';

  useEffect(() => {
    if (isNewUser) {
      // Clear the query param and redirect to onboarding
      navigate('/onboarding', { replace: true });
    }
  }, [isNewUser, navigate]);

  return null;
}

function App() {
  useKioskAutoReload();
  const [isLoading, setIsLoading] = useState(true);
  const setUser = useAuthStore((state) => state.setUser);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    async function checkAuth() {
      try {
        const userData = await fetchAuthMe();
        setUser({
          id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          familyId: userData.familyId,
        });
      } catch {
        // Auth check failed - clear stale persisted state
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }
    checkAuth();
  }, [setUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-text-secondary">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <AuthRedirectHandler />
      <KioskReconnectOverlay />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Public landing page - accessible even when logged in */}
        <Route path="/home" element={<LandingPage />} />
        {/* Public legal pages */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        {/* Kiosk pairing: deep link for admins to generate pairing codes */}
        <Route path="/pair" element={<PairPage />} />
        {/* Test pages (dev only) */}
        {import.meta.env.DEV && (
          <Route path="/test/address-autocomplete" element={<TestAddressAutocompletePage />} />
        )}
        {/* Public invitation routes */}
        <Route path="/invite/:token" element={<AcceptInvitePage />} />
        <Route path="/invite/expired" element={<InviteExpiredPage />} />
        <Route path="/invite/conflict" element={<InviteConflictPage />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <ErrorBoundary>
                <Layout>
                  <Routes>
                    <Route path="/" element={<CalendarPage />} />
                    <Route path="/calendar" element={<Navigate to="/" replace />} />
                    <Route path="/chores" element={<ChoresPage />} />
                    <Route path="/lists" element={<ListsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                  </Routes>
                </Layout>
              </ErrorBoundary>
            ) : (
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            )
          }
        />
      </Routes>
    </>
  );
}

export default App;
